import React from 'react';
import PropTypes from 'prop-types';
import Pikaday from 'pikaday';

import { format } from '../../../constants/dates';
import { addDays, isValidDateObject } from '../../../util/date';

import PopOut from './PopOut';
import Icon from '../Icon';
import Form from '../../modules/Form';

const PikadayControlCol = React.forwardRef((
  {
    disabled,
    id,
    placeholder,
    onBlur,
    onFocus,
  },
  inputRef,
) => (
  <div className="Form_controlCol">
    <Icon name="Date" className="Form_inlineIcon" />
    <input
      className="Form_text Form_text-datePicker"
      disabled={disabled}
      id={id}
      type="text"
      readOnly
      placeholder={placeholder}
      data-form="input"
      onBlur={onBlur}
      onFocus={onFocus}
      ref={inputRef}
      ></input>
    <label className="Form_controlLabel" htmlFor={id}>{placeholder}</label>
  </div>
));

PikadayControlCol.propTypes = {
  disabled: PropTypes.bool,
  id: PropTypes.string,
  placeholder: PropTypes.string,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
};


class DatesFromTo extends React.Component {
  fromPikaday = undefined;

  toPikaday = undefined;

  fromInputDisabledMutationObserver = undefined;

  toInputDisabledMutationObserver = undefined;

  fromInputRef = React.createRef();

  toInputRef = React.createRef();

  constructor() {
    super();
    this.onPikadayFromSelectDate = this.onPikadayFromSelectDate.bind(this);
    this.onPikadayToSelectDate = this.onPikadayToSelectDate.bind(this);
  }


  destroy() {
    if (this.fromPikaday) {
      this.fromPikaday.destroy();
    }
    if (this.toPikaday) {
      this.toPikaday.destroy();
    }
  }


  static onBlur(event) {
    Form.onInputBlur(event);
  }


  static onFocus(event) {
    Form.onInputFocus(event);
  }


  initPikaday() {
    this.destroy();

    this.fromPikaday = new Pikaday({
      defaultDate: this.props.fromDate,
      setDefaultDate: true,
      format,
      field: this.fromInputRef.current,
      minDate: new Date(),
      onSelect: this.onPikadayFromSelectDate,
    });

    this.toPikaday = new Pikaday({
      defaultDate: this.props.toDate,
      setDefaultDate: true,
      format,
      field: this.toInputRef.current,
      minDate: new Date(),
      onSelect: this.onPikadayToSelectDate,
    });
  }


  onPikadayFromSelectDate(date) {
    const fromTime = isValidDateObject(date) ? date.getTime() : 0;
    const propsFromTime = isValidDateObject(this.props.fromDate)
      ? this.props.fromDate.getTime() : 0;

    if (fromTime !== propsFromTime) {
      this.props.onFromChange(date);
    }
  }


  onPikadayToSelectDate(date) {
    const toTime = isValidDateObject(date) ? date.getTime() : 0;
    const propsToTime = isValidDateObject(this.props.toDate)
      ? this.props.toDate.getTime() : 0;

    if (toTime !== propsToTime) {
      this.props.onToChange(date);
    }
  }


  componentDidMount() {
    const {
      disabled,
    } = this.props;

    this.fromInputDisabledMutationObserver
      = Form.createInputDisabledMutationObserver(this.fromInputRef.current);
    this.toInputDisabledMutationObserver
      = Form.createInputDisabledMutationObserver(this.toInputRef.current);

    if (!disabled) {
      this.initPikaday();
    }
  }


  componentDidUpdate() {
    const currentFromDate = this.fromPikaday.getDate();
    const currentFromTime = currentFromDate ? currentFromDate.getTime() : null;
    const fromTime = isValidDateObject(this.props.fromDate) ? this.props.fromDate.getTime() : null;

    if (fromTime !== currentFromTime) {
      this.fromPikaday.setDate(isValidDateObject(this.props.fromDate) ? this.props.fromDate : null);
    }

    const currentToDate = this.toPikaday.getDate();
    const currentToTime = currentToDate ? currentToDate.getTime() : null;
    const toTime = isValidDateObject(this.props.toDate) ? this.props.toDate.getTime() : null;

    if (toTime !== currentToTime) {
      this.toPikaday.setDate(isValidDateObject(this.props.toDate) ? this.props.toDate : null);
    }

    if (isValidDateObject(this.props.fromDate)) {
      this.toPikaday.setMinDate(addDays(1, this.props.fromDate));
      this.toPikaday.gotoDate(this.props.fromDate);
    }
  }


  componentWillUnmount() {
    this.fromInputDisabledMutationObserver.disconnect();
    this.toInputDisabledMutationObserver.disconnect();
    this.destroy();
  }


  render() {
    const {
      disabled,
      fromPlaceholder,
      id,
      info,
      label,
      toPlaceholder,
    } = this.props;

    return (
      <div className="Form_control" data-form="control">
        <div className="Form_controlCols">
          <PikadayControlCol
            disabled={disabled}
            id={`${id}FromDate`}
            placeholder={fromPlaceholder}
            onBlur={DatesFromTo.onBlur}
            onFocus={DatesFromTo.onFocus}
            ref={this.fromInputRef}
          />
          <PikadayControlCol
            disabled={disabled}
            id={`${id}ToDate`}
            placeholder={toPlaceholder}
            onBlur={DatesFromTo.onBlur}
            onFocus={DatesFromTo.onFocus}
            ref={this.toInputRef}
          />
        </div>
        <legend className="Form_controlLabel">{label}</legend>
        {info &&
          <PopOut info={info}>
            <button type="button" className="Form_popOutHit Form_popOutHit-right">i</button>
          </PopOut>
        }
      </div>
    );
  }
}


DatesFromTo.defaultProps = {
  disabled: false,
  placeholder: '',
  value: '',
};


DatesFromTo.propTypes = {
  disabled: PropTypes.bool,
  fromDate: PropTypes.instanceOf(Date),
  fromPlaceholder: PropTypes.string,
  id: PropTypes.string.isRequired,
  info: PropTypes.string,
  label: PropTypes.string.isRequired,
  onFromChange: PropTypes.func,
  onToChange: PropTypes.func,
  toDate: PropTypes.instanceOf(Date),
  toPlaceholder: PropTypes.string,
};


export default DatesFromTo;
